import { useCallback, useEffect, useState } from 'react';
import type { WearApiClient } from '../api/WearApiClient';
import type { WearOutput, WearQuery } from '../type/WearTypes';

export const WEAR_DEBOUNCE_MS = 300;

export interface WearError {
  code: number;
  name: string;
  message: string;
}

interface Resolved {
  key: string;
  data: WearOutput | null;
  error: WearError | null;
}

/**
 * Live tool-wear computation: every query change calls wear/read after a short
 * pause, cancelling the previous request. `data` keeps the last result of the
 * same tool while the next one loads, so the cards do not flash.
 */
export function useWearPrediction(client: WearApiClient | null, query: WearQuery | null) {
  const key = query ? JSON.stringify(query) : null;
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!client || key === null) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      client
        .read(JSON.parse(key) as WearQuery, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return;
          if (res.ok && res.data?.output) {
            setResolved({ key, data: res.data.output, error: null });
          } else if (res.ok) {
            // 202: the worker did not answer in time
            setResolved({ key, data: null, error: { code: 202, name: 'Pending', message: '' } });
          } else {
            setResolved({
              key,
              data: null,
              error: { code: res.code, name: res.name, message: res.message ?? '' },
            });
          }
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError'))
            return;
          setResolved({
            key,
            data: null,
            error: { code: 0, name: 'NetworkError', message: String(error) },
          });
        });
    }, WEAR_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [client, key, tick]);

  const retry = useCallback(() => {
    setResolved(null);
    setTick((value) => value + 1);
  }, []);

  if (key === null) {
    return { data: null, error: null, isLoading: false, retry };
  }
  const current = resolved?.key === key ? resolved : null;
  const sameTool =
    resolved?.data && query && resolved.data.toolId === query.toolId ? resolved.data : null;
  return {
    data: current ? current.data : sameTool,
    error: current ? current.error : null,
    isLoading: current === null,
    retry,
  };
}
